type Difficulty = 'Easy' | 'Medium' | 'Hard';

interface Problem {
  id: string;
  title: string;
  difficulty: Difficulty;
  description: string;
  examples: Array<{
    input: string;
    output: string;
    explanation?: string;
  }>;
  starterCode: Record<string, string>;
  testCases: Array<{
    input: string;
    output: string;
  }>;
}

export const arrayProblems: Problem[] = [
  {
    id: 'arrays-1',
    title: 'Two Sum',
    difficulty: 'Easy',
    description: `Given an array of integers nums and an integer target, return indices of the two numbers in the array such that they add up to target.

You may assume that each input would have exactly one solution, and you may not use the same element twice.

You can return the answer in any order.

**Constraints:**
- 2 <= nums.length <= 10^4
- -10^9 <= nums[i] <= 10^9
- -10^9 <= target <= 10^9
- Only one valid answer exists`,
    examples: [
      {
        input: 'nums = [2,7,11,15], target = 9',
        output: '[0,1]',
        explanation: 'Because nums[0] + nums[1] == 9, we return [0, 1].'
      },
      {
        input: 'nums = [3,2,4], target = 6',
        output: '[1,2]',
        explanation: 'Because nums[1] + nums[2] == 6, we return [1, 2].'
      },
      {
        input: 'nums = [3,3], target = 6',
        output: '[0,1]',
        explanation: 'Because nums[0] + nums[1] == 6, we return [0, 1].'
      }
    ],
    starterCode: {
      python: `# DO NOT MODIFY THIS SECTION
def parse_input():
    # Read array
    nums_str = input().strip()
    nums = eval(nums_str)  # Safe since input is controlled
    
    # Read target
    target = int(input().strip())
    
    return nums, target

def format_output(result):
    return str(result)

def twoSum(nums: list[int], target: int) -> list[int]:
    """
    Find indices of two numbers that add up to target
    Args:
        nums: List of integers
        target: Target sum
    Returns:
        List of two indices
    """
    pass

if __name__ == "__main__":
    nums, target = parse_input()
    result = twoSum(nums, target)
    print(format_output(result))`,
      javascript: `// DO NOT MODIFY THIS SECTION
function parseInput() {
    // Read array and target from stdin
    const input = require('fs').readFileSync(0, 'utf-8').trim().split('\\n');
    const nums = JSON.parse(input[0]);
    const target = parseInt(input[1]);
    return [nums, target];
}

function formatOutput(result) {
    return JSON.stringify(result);
}

/**
 * @param {number[]} nums
 * @param {number} target
 * @return {number[]}
 */
function twoSum(nums, target) {
    // Write your code here
}

// Main execution
const [nums, target] = parseInput();
const result = twoSum(nums, target);
console.log(formatOutput(result));`,
      cpp: `#include <iostream>
#include <vector>
#include <string>
#include <sstream>

// DO NOT MODIFY THIS SECTION
std::vector<int> parseArray(const std::string& input) {
    std::vector<int> result;
    std::stringstream ss(input.substr(1, input.size()-2));
    std::string item;
    while (std::getline(ss, item, ',')) {
        result.push_back(std::stoi(item));
    }
    return result;
}

std::string formatArray(const std::vector<int>& arr) {
    std::stringstream ss;
    ss << "[";
    for (size_t i = 0; i < arr.size(); ++i) {
        if (i > 0) ss << ",";
        ss << arr[i];
    }
    ss << "]";
    return ss.str();
}

class Solution {
public:
    vector<int> twoSum(vector<int>& nums, int target) {
        // Write your code here
    }
};

int main() {
    // Read input
    std::string line;
    std::getline(std::cin, line);
    std::vector<int> nums = parseArray(line);
    
    std::getline(std::cin, line);
    int target = std::stoi(line);
    
    // Solve and output
    Solution solution;
    std::vector<int> result = solution.twoSum(nums, target);
    std::cout << formatArray(result) << std::endl;
    
    return 0;
}`,
      java: `import java.util.*;

// DO NOT MODIFY THIS SECTION
class Solution {
    private static int[] parseArray(String input) {
        input = input.substring(1, input.length() - 1);
        if (input.isEmpty()) return new int[0];
        String[] parts = input.split(",");
        int[] result = new int[parts.length];
        for (int i = 0; i < parts.length; i++) {
            result[i] = Integer.parseInt(parts[i].trim());
        }
        return result;
    }
    
    private static String formatArray(int[] arr) {
        StringBuilder sb = new StringBuilder();
        sb.append("[");
        for (int i = 0; i < arr.length; i++) {
            if (i > 0) sb.append(",");
            sb.append(arr[i]);
        }
        sb.append("]");
        return sb.toString();
    }
    
    public int[] twoSum(int[] nums, int target) {
        // Write your code here
    }
    
    public static void main(String[] args) {
        Scanner scanner = new Scanner(System.in);
        
        // Read input
        int[] nums = parseArray(scanner.nextLine());
        int target = Integer.parseInt(scanner.nextLine());
        
        // Solve and output
        Solution solution = new Solution();
        int[] result = solution.twoSum(nums, target);
        System.out.println(formatArray(result));
    }
}`
    },
    testCases: [
      {
        input: '[2,7,11,15]\n9',
        output: '[0,1]'
      },
      {
        input: '[3,2,4]\n6',
        output: '[1,2]'
      },
      {
        input: '[3,3]\n6',
        output: '[0,1]'
      },
      {
        input: '[1,5,8,10,13,2,3,9]\n15',
        output: '[2,7]'
      },
      {
        input: '[-1,-2,-3,-4,-5]\n-8',
        output: '[2,4]'
      },
      {
        input: '[1000000000,1000000000]\n2000000000',
        output: '[0,1]'
      },
      {
        input: '[0,0,0,0]\n0',
        output: '[0,1]'
      },
      {
        input: '[1,2,3,4,5,6,7,8,9,10]\n19',
        output: '[8,9]'
      }
    ]
  },
  {
    id: 'arrays-2',
    title: 'Maximum Subarray',
    difficulty: 'Easy',
    description: `Given an integer array \`nums\`, find the subarray with the largest sum, and return its sum.

A subarray is a contiguous non-empty sequence of elements within an array.`,
    examples: [
      {
        input: 'nums = [-2,1,-3,4,-1,2,1,-5,4]',
        output: '6',
        explanation: 'The subarray [4,-1,2,1] has the largest sum 6.'
      }
    ],
    starterCode: {
      python: `def maxSubArray(nums):
    """
    Find the contiguous subarray with the largest sum
    
    Args:
        nums: List[int] - An array of integers
        
    Returns:
        int - The sum of the subarray with the largest sum
    """
    # Write your code here
    pass

# DO NOT MODIFY BELOW THIS LINE
# Test with a hardcoded example
nums = [-2, 1, -3, 4, -1, 2, 1, -5, 4]
result = maxSubArray(nums)
print(result)`,
      javascript: `/**
 * Find the contiguous subarray with the largest sum
 *
 * @param {number[]} nums - An array of integers
 * @return {number} - The sum of the subarray with the largest sum
 */
function maxSubArray(nums) {
    // Write your code here
}

// DO NOT MODIFY BELOW THIS LINE
// Parse input
const input = process.stdin.toString().trim();
const nums = JSON.parse(input);

// Get result
const result = maxSubArray(nums);
console.log(result);`,
      cpp: `#include <vector>
#include <iostream>
#include <string>
#include <sstream>

/**
 * Find the contiguous subarray with the largest sum
 *
 * @param nums - Vector of integers
 * @return - The sum of the subarray with the largest sum
 */
int maxSubArray(std::vector<int>& nums) {
    // Write your code here
}

// DO NOT MODIFY BELOW THIS LINE
std::vector<int> parseArray(const std::string& input) {
    std::vector<int> result;
    // Remove brackets and split by comma
    std::stringstream ss(input.substr(1, input.size()-2));
    std::string item;
    while (std::getline(ss, item, ',')) {
        result.push_back(std::stoi(item));
    }
    return result;
}

int main() {
    std::string line;
    std::getline(std::cin, line);
    auto nums = parseArray(line);
    
    auto result = maxSubArray(nums);
    std::cout << result;
    return 0;
}`,
      java: `import java.util.*;

/**
 * Solution class for the Maximum Subarray problem
 */
class Solution {
    /**
     * Find the contiguous subarray with the largest sum
     *
     * @param nums - Array of integers
     * @return - The sum of the subarray with the largest sum
     */
    public static int maxSubArray(int[] nums) {
        // Write your code here
        return 0; // Placeholder return
    }

    // DO NOT MODIFY BELOW THIS LINE
    public static void main(String[] args) {
        Scanner scanner = new Scanner(System.in);
        
        // Parse input array
        String line = scanner.nextLine();
        String[] parts = line.substring(1, line.length()-1).split(",");
        int[] nums = new int[parts.length];
        for (int i = 0; i < parts.length; i++) {
            nums[i] = Integer.parseInt(parts[i].trim());
        }
        
        // Get result
        int result = maxSubArray(nums);
        System.out.print(result);
        scanner.close();
    }
}`
    },
    testCases: [
      {
        input: '[-2,1,-3,4,-1,2,1,-5,4]',
        output: '6'
      }
    ]
  },
  {
    id: 'arrays-3',
    title: 'Container With Most Water',
    difficulty: 'Medium',
    description: `You are given an integer array \`height\` of length \`n\`. There are \`n\` vertical lines drawn such that the two endpoints of the \`ith\` line are \`(i, 0)\` and \`(i, height[i])\`.

Find two lines that together with the x-axis form a container, such that the container contains the most water.

Return the maximum amount of water a container can store.`,
    examples: [
      {
        input: 'height = [1,8,6,2,5,4,8,3,7]',
        output: '49',
        explanation: 'The above vertical lines are represented by array [1,8,6,2,5,4,8,3,7]. In this case, the max area of water (blue section) the container can contain is 49.'
      }
    ],
    starterCode: {
      python: `def maxArea(height):
    """
    Find the maximum area of water that can be trapped
    
    Args:
        height: List[int] - An array of heights
        
    Returns:
        int - The maximum area of water that can be trapped
    """
    # Write your code here
    pass

# DO NOT MODIFY BELOW THIS LINE
# Test with a hardcoded example
height = [1, 8, 6, 2, 5, 4, 8, 3, 7]
result = maxArea(height)
print(result)`,
      javascript: `/**
 * Find the maximum area of water that can be trapped
 *
 * @param {number[]} height - An array of heights
 * @return {number} - The maximum area of water that can be trapped
 */
function maxArea(height) {
    // Write your code here
}

// DO NOT MODIFY BELOW THIS LINE
// Parse input
const input = process.stdin.toString().trim();
const height = JSON.parse(input);

// Get result
const result = maxArea(height);
console.log(result);`,
      cpp: `#include <vector>
#include <iostream>
#include <string>
#include <sstream>

/**
 * Find the maximum area of water that can be trapped
 *
 * @param height - Vector of heights
 * @return - The maximum area of water that can be trapped
 */
int maxArea(std::vector<int>& height) {
    // Write your code here
}

// DO NOT MODIFY BELOW THIS LINE
std::vector<int> parseArray(const std::string& input) {
    std::vector<int> result;
    // Remove brackets and split by comma
    std::stringstream ss(input.substr(1, input.size()-2));
    std::string item;
    while (std::getline(ss, item, ',')) {
        result.push_back(std::stoi(item));
    }
    return result;
}

int main() {
    std::string line;
    std::getline(std::cin, line);
    auto height = parseArray(line);
    
    auto result = maxArea(height);
    std::cout << result;
    return 0;
}`,
      java: `import java.util.*;

/**
 * Solution class for the Container With Most Water problem
 */
class Solution {
    /**
     * Find the maximum area of water that can be trapped
     *
     * @param height - Array of heights
     * @return - The maximum area of water that can be trapped
     */
    public static int maxArea(int[] height) {
        // Write your code here
        return 0; // Placeholder return
    }

    // DO NOT MODIFY BELOW THIS LINE
    public static void main(String[] args) {
        Scanner scanner = new Scanner(System.in);
        
        // Parse input array
        String line = scanner.nextLine();
        String[] parts = line.substring(1, line.length()-1).split(",");
        int[] height = new int[parts.length];
        for (int i = 0; i < parts.length; i++) {
            height[i] = Integer.parseInt(parts[i].trim());
        }
        
        // Get result
        int result = maxArea(height);
        System.out.print(result);
        scanner.close();
    }
}`
    },
    testCases: [
      {
        input: '[1,8,6,2,5,4,8,3,7]',
        output: '49'
      }
    ]
  },
  {
    id: 'arrays-4',
    title: 'First Missing Positive',
    difficulty: 'Hard',
    description: `Given an unsorted integer array \`nums\`, return the smallest missing positive integer.

You must implement an algorithm that runs in O(n) time and uses O(1) auxiliary space.`,
    examples: [
      {
        input: 'nums = [1,2,0]',
        output: '3',
        explanation: 'The numbers in the range [1,2] are all in the array.'
      },
      {
        input: 'nums = [3,4,-1,1]',
        output: '2',
        explanation: '2 is the smallest missing positive integer.'
      }
    ],
    starterCode: {
      python: `def firstMissingPositive(nums):
    """
    Find the smallest missing positive integer
    
    Args:
        nums: List[int] - An array of integers
        
    Returns:
        int - The smallest missing positive integer
    """
    # Write your code here
    pass

# DO NOT MODIFY BELOW THIS LINE
# Test with a hardcoded example
nums = [1, 2, 0]
result = firstMissingPositive(nums)
print(result)`,
      javascript: `/**
 * Find the smallest missing positive integer
 *
 * @param {number[]} nums - An array of integers
 * @return {number} - The smallest missing positive integer
 */
function firstMissingPositive(nums) {
    // Write your code here
}

// DO NOT MODIFY BELOW THIS LINE
// Parse input
const input = process.stdin.toString().trim();
const nums = JSON.parse(input);

// Get result
const result = firstMissingPositive(nums);
console.log(result);`,
      cpp: `#include <vector>
#include <iostream>
#include <string>
#include <sstream>

/**
 * Find the smallest missing positive integer
 *
 * @param nums - Vector of integers
 * @return - The smallest missing positive integer
 */
int firstMissingPositive(std::vector<int>& nums) {
    // Write your code here
}

// DO NOT MODIFY BELOW THIS LINE
std::vector<int> parseArray(const std::string& input) {
    std::vector<int> result;
    // Remove brackets and split by comma
    std::stringstream ss(input.substr(1, input.size()-2));
    std::string item;
    while (std::getline(ss, item, ',')) {
        result.push_back(std::stoi(item));
    }
    return result;
}

int main() {
    std::string line;
    std::getline(std::cin, line);
    auto nums = parseArray(line);
    
    auto result = firstMissingPositive(nums);
    std::cout << result;
    return 0;
}`,
      java: `import java.util.*;

/**
 * Solution class for the First Missing Positive problem
 */
class Solution {
    /**
     * Find the smallest missing positive integer
     *
     * @param nums - Array of integers
     * @return - The smallest missing positive integer
     */
    public static int firstMissingPositive(int[] nums) {
        // Write your code here
        return 1; // Placeholder return
    }

    // DO NOT MODIFY BELOW THIS LINE
    public static void main(String[] args) {
        Scanner scanner = new Scanner(System.in);
        
        // Parse input array
        String line = scanner.nextLine();
        String[] parts = line.substring(1, line.length()-1).split(",");
        int[] nums = new int[parts.length];
        for (int i = 0; i < parts.length; i++) {
            nums[i] = Integer.parseInt(parts[i].trim());
        }
        
        // Get result
        int result = firstMissingPositive(nums);
        System.out.print(result);
        scanner.close();
    }
}`
    },
    testCases: [
      {
        input: '[1,2,0]',
        output: '3'
      }
    ]
  },
  {
    id: 'arrays-5',
    title: 'Median of Two Sorted Arrays',
    difficulty: 'Hard',
    description: `Given two sorted arrays \`nums1\` and \`nums2\` of size \`m\` and \`n\` respectively, return the median of the two sorted arrays.

The overall run time complexity should be O(log (m+n)).`,
    examples: [
      {
        input: 'nums1 = [1,3], nums2 = [2]',
        output: '2.0',
        explanation: 'Merged array = [1,2,3] and median is 2.'
      },
      {
        input: 'nums1 = [1,2], nums2 = [3,4]',
        output: '2.5',
        explanation: 'Merged array = [1,2,3,4] and median is (2 + 3) / 2 = 2.5.'
      }
    ],
    starterCode: {
      python: `def findMedianSortedArrays(nums1, nums2):
    """
    Find the median of two sorted arrays
    
    Args:
        nums1: List[int] - First array
        nums2: List[int] - Second array
        
    Returns:
        float - The median of the two sorted arrays
    """
    # Write your code here
    pass

# DO NOT MODIFY BELOW THIS LINE
# Test with a hardcoded example
nums1 = [1, 3]
nums2 = [2]
result = findMedianSortedArrays(nums1, nums2)
print(result)`,
      javascript: `/**
 * Find the median of two sorted arrays
 *
 * @param {number[]} nums1 - First array
 * @param {number[]} nums2 - Second array
 * @return {number} - The median of the two sorted arrays
 */
function findMedianSortedArrays(nums1, nums2) {
    // Write your code here
}

// DO NOT MODIFY BELOW THIS LINE
// Parse input
const input = process.stdin.toString().trim().split('\n');
const nums1 = JSON.parse(input[0]);
const nums2 = JSON.parse(input[1]);

// Get result
const result = findMedianSortedArrays(nums1, nums2);
console.log(result);`,
      cpp: `#include <vector>
#include <iostream>
#include <string>
#include <sstream>

/**
 * Find the median of two sorted arrays
 *
 * @param nums1 - First vector
 * @param nums2 - Second vector
 * @return - The median of the two sorted arrays
 */
double findMedianSortedArrays(std::vector<int>& nums1, std::vector<int>& nums2) {
    // Write your code here
}

// DO NOT MODIFY BELOW THIS LINE
std::vector<int> parseArray(const std::string& input) {
    std::vector<int> result;
    // Remove brackets and split by comma
    std::stringstream ss(input.substr(1, input.size()-2));
    std::string item;
    while (std::getline(ss, item, ',')) {
        result.push_back(std::stoi(item));
    }
    return result;
}

int main() {
    std::string line1, line2;
    std::getline(std::cin, line1);
    std::getline(std::cin, line2);
    
    auto nums1 = parseArray(line1);
    auto nums2 = parseArray(line2);
    
    auto result = findMedianSortedArrays(nums1, nums2);
    std::cout << result;
    return 0;
}`,
      java: `import java.util.*;

/**
 * Solution class for the Median of Two Sorted Arrays problem
 */
class Solution {
    /**
     * Find the median of two sorted arrays
     *
     * @param nums1 - First array
     * @param nums2 - Second array
     * @return - The median of the two sorted arrays
     */
    public static double findMedianSortedArrays(int[] nums1, int[] nums2) {
        // Write your code here
        return 0.0; // Placeholder return
    }

    // DO NOT MODIFY BELOW THIS LINE
    public static void main(String[] args) {
        Scanner scanner = new Scanner(System.in);
        
        // Parse first array
        String line1 = scanner.nextLine();
        String[] parts1 = line1.substring(1, line1.length()-1).split(",");
        int[] nums1 = new int[parts1.length];
        for (int i = 0; i < parts1.length; i++) {
            nums1[i] = Integer.parseInt(parts1[i].trim());
        }
        
        // Parse second array
        String line2 = scanner.nextLine();
        String[] parts2 = line2.substring(1, line2.length()-1).split(",");
        int[] nums2 = new int[parts2.length];
        for (int i = 0; i < parts2.length; i++) {
            nums2[i] = Integer.parseInt(parts2[i].trim());
        }
        
        // Get result
        double result = findMedianSortedArrays(nums1, nums2);
        System.out.printf("%.1f", result);
        scanner.close();
    }
}`
    },
    testCases: [
      {
        input: '[1,3]\n[2]',
        output: '2.0'
      }
    ]
  }
];
